import {
  AgreementType,
  BadRequestError,
  BaseUserProfile,
  Gender,
  HandledError,
  IInfluencerSettings,
  IPaymentService,
  IStripeCustomerDetailsWrapper,
  KnownMediums,
  LeadStatus,
  LegalAgreement,
  ProgramType,
} from 'fitbeat.models';
import uuidv4 from 'uuid/v4';
import { DynamicLinkService } from '../logic/dynamic-link/dynamic-link-service';
import { IUserProfileRepository } from '../persistence';
import { Level } from './exercise/exercise';
import { UserProfile } from './user/userprofile';

function addLegalAgreements(profile: UserProfile, params: any): UserProfile {
  const buildLegalAgreements = (legalAgreements: string[]): LegalAgreement[] => legalAgreements.map(
    (agreement: AgreementType) => LegalAgreement.createSignedInstance(agreement),
  );

  function mergeLegalAgreements(
    previousAgreements: LegalAgreement[] | undefined, // undefined when building new user profile
    newAgreements: LegalAgreement[],
  ): LegalAgreement[] {
    if (previousAgreements === undefined) {
      return newAgreements;
    }

    const agreementsNotAlreadyAccepted = newAgreements
      .filter((newAgreement: LegalAgreement) => {
        const foundInPrevious = previousAgreements
          // TODO: eventually this should check on the version as well
          .find((previous) => previous.agreement === newAgreement.agreement);
        return !foundInPrevious;
      });

    return previousAgreements.concat(agreementsNotAlreadyAccepted);
  }

  if (params.legalAgreements) {
    const newAgreements = buildLegalAgreements(params.legalAgreements);
    profile.legalAgreements = mergeLegalAgreements(profile.legalAgreements, newAgreements);
  }

  const shouldAddGuardianTerms = params.guardianInfo && params.guardianInfo.legalAgreements;
  if (shouldAddGuardianTerms) {
    const newAgreements = buildLegalAgreements(params.guardianInfo.legalAgreements);
    profile.guardianInfo.legalAgreements = mergeLegalAgreements(profile.guardianInfo.legalAgreements, newAgreements);
  }

  if (!params.legalAgreements && !shouldAddGuardianTerms && profile.legalAgreements === undefined) {
    profile.legalAgreements = [];
  }

  return profile;
}

export function initializeUserDetails(params: any): UserProfile {
  const profile = new UserProfile(params.uid, {
    dateOfBirth: params.dateOfBirth, // used in heartRateMax calculation
    // pass in readonly fields
  });
  const date = new Date().toUTCString();
  // TODO: consider using a combination of `mapUserUpdateableFields`+constructor+setting fields afterwards
  profile.email = params.email ? params.email.toLowerCase() : params.email;
  profile.username = params.email;
  profile.userSignupDate = date;
  profile.planHistory = [];
  profile.firstname = params.firstname;
  profile.lastname = params.lastname;
  profile.guardianInfo = params.guardianInfo;
  profile.gender = params.gender;
  profile.branch = params.branch;
  profile.fullname = BaseUserProfile.buildFullname(profile);
  profile.height = Number(params.height) || 0;
  profile.mobileNumber = formatMobileNumber(params.mobileNumber);
  profile.shouldShowWelcomeVideo = false;
  profile.shouldShowTrackingOnboarding = true;
  profile.country = params.country;
  profile.useImperialUnit = params.useImperialUnit || false;
  profile.leadStatus = LeadStatus.Warm;
  profile.stripeUserId = params.stripeUserId;
  profile.externalReference = params.externalReference;
  profile.companyId = params.companyId;
  profile.employeeId = params.employeeId;
  profile.gymName = params.gymName;
  if (params.signupSource) {
    profile.signupSource = {
      ...params.signupSource,
      medium: params.signupSource.medium || params.medium,
      signupWorkoutInviteId: params.signupWorkoutInviteId,
    };
  }
  addLegalAgreements(profile, params);
  (profile as any)._id = params.uid;
  return profile;
}

export function updateBasicUserInfoAfterInitialAuthSetup(existing: UserProfile, newParams: any): UserProfile {
  if (!newParams) {
    throw new BadRequestError({
      details: 'You need to post a valid user profile',
    });
  }

  if (!newParams.email) {
    throw new Error('Missing user contact details');
  }

  existing.dateOfBirth = newParams.dateOfBirth;
  existing.guardianInfo = newParams.guardianInfo;
  existing.country = newParams.country;
  addLegalAgreements(existing, newParams);
  return existing;
}

export function initializePreUserAccount(email: string, mobileNumber: string): any {
  const id = uuidv4();
  return {
    _id: id,
    uid: id,
    email,
    mobileNumber: formatMobileNumber(mobileNumber),
  };
}

export function initializeUserFromSubsetOfFields(
  params: any,
  firebaseUserId: string | null,
  inputValidationFunction: (fields: any) => void,
): UserProfile {
  const fields = { ...params, uid: firebaseUserId || uuidv4() };
  inputValidationFunction(fields);
  const user = initializeUserDetails(fields);
  return user;
}

export async function createContinueSignupLink(
  user: UserProfile,
  dynamicLinkService: DynamicLinkService,
) {
  user.continueSignupToken = uuidv4();
  user.continueSignupLink = await dynamicLinkService.createContinueSignupLink(user.uid, user.continueSignupToken);
}

export function validatePreAccountDetailsForLoginTerminal(fields: any): void {
  // todo: add more validations on input params (medical checks, guardian, etc)
  if (!fields.firstname || !fields.lastname || !fields.dateOfBirth) {
    throw new Error('Missing basic user details');
  }
  if (!fields.email) {
    throw new Error('Missing user contact details');
  }
}

export function validatePreAccountDetailsForAppSignup(fields: any): void {
  // todo: add more validations on input params (medical checks, guardian, etc)
  if (!fields.email) {
    throw new Error('Email is required');
  }

  if (!fields.mobileNumber) {
    throw new Error('Mobile number is required');
  }
}

export function initializeStripeDetails(
  userProfile: UserProfile,
  stripeUser: IStripeCustomerDetailsWrapper,
  initialPlanId: string,
  paymentService: IPaymentService,
): void {
  userProfile.stripeUserId = stripeUser.id;
  userProfile.currentPlanId = initialPlanId;
}

export function updateLevelAndGoalOnUser(user: UserProfile, skillLevel: Level, goal: ProgramType): UserProfile {
  if (!(Object as any).values(Level).includes(skillLevel)) {
    throw new Error(`Unable to set user level: '${skillLevel}' is not a valid Level`);
  }
  if (!(Object as any).values(ProgramType).includes(goal)) {
    throw new Error(`Unable to set user goal: '${goal}' is not a valid ProgramType`);
  }
  user.skillLevel = skillLevel;
  user.longTermGoal = goal;
  return user;
}

export function updateWeightHeightAndStudioHRMonitorOnUser(
  user: UserProfile,
  weight: string,
  height: string,
  hrMonitor: string,
): UserProfile {
  if (!BaseUserProfile.hasValidWeight(+weight)) {
    throw new Error('Unable to set user weight ,field is not valid');
  }
  user.weight = +weight;
  user.height = +height;
  user.heartRateMonitors = {
    ...user.heartRateMonitors,
    studio: hrMonitor,
  };
  return user;
}

export async function updateMemberDetailsOnUser(
  user: UserProfile,
  userRepo: IUserProfileRepository,
  mobileNumber: string,
  email: string,
  skillLevel: Level,
  longTermGoal: ProgramType,
  heartRateMonitorIdentifier: string,
  dateOfBirth: any,
  gender: Gender,
  addressLine1: string,
  addressLine2: string,
  postCode: string,
  suburb: string,
  unit: string,
  state: string,
  weight: number,
  height: number,
  nickname: string,
  firstname: string,
  lastname: string,
  bio: string,
  isStreamingUser: boolean,
  discount: number,
  source: string,
  attributionSourceId: string,
  signupSource: string,
): Promise<any> {
  const validMobileNumber = mobileNumber
    ? UserProfile.isHomeAccountType(user) || UserProfile.hasValidPhoneNumber(mobileNumber) : true;
  const validEmail = email ? UserProfile.hasValidEmail(email) : true;
  const validSkillLevel = skillLevel ? UserProfile.hasValidSkillLevel(skillLevel) : true;
  const validLongTermGoal = longTermGoal ? UserProfile.hasValidLongTermGoal(longTermGoal) : true;
  const validGender = gender ? UserProfile.hasValidGender(gender) : true;
  const validDateOfBirth = dateOfBirth ? UserProfile.hasValidDateOfBirth(dateOfBirth) : true;
  const validAddressLine1 = addressLine1 ? UserProfile.checkMaxLength({ addressLine1 }, 50) : true;
  const validAddressLine2 = addressLine2 ? UserProfile.checkMaxLength({ addressLine2 }, 50) : true;
  const validPostCode = postCode ? UserProfile.checkMaxLength({ postCode }, 50) : true;
  const validSuburb = suburb ? UserProfile.checkMaxLength({ suburb }, 50) : true;
  const validUnitNum = unit ? UserProfile.checkMaxLength({ unit }, 50) : true;
  const validState = state ? UserProfile.checkMaxLength({ state }, 50) : true;
  const validWeight = weight ? UserProfile.checkNumberRange({ weight }, 20, 300) : true;
  const validHeight = height ? UserProfile.checkNumberRange({ height }, 20, 300) : true;
  const validNickname = nickname ? UserProfile.isHomeAccountType(user)
    || (UserProfile.isRequired({ nickname }) && UserProfile.checkMaxLength({ nickname }, 50)) : true;
  const validFirstname = firstname
    ? UserProfile.isRequired({ firstname }) && UserProfile.checkMaxLength({ firstname }, 50) : true;
  const validLastname = lastname
    ? UserProfile.isRequired({ lastname }) && UserProfile.checkMaxLength({ lastname }, 50) : true;
  const tempDisablingInfluencerUpdating = true;
  const hasInfluencerFields = (discount || source) && !tempDisablingInfluencerUpdating;
  const validInfluencerSettings = !hasInfluencerFields || (hasInfluencerFields
    && UserProfile.hasValidInfluencerSettings(discount, source));
  let validBio = true;
  if (bio) {
    const bioMaxCharacterCount = BaseUserProfile.maxLengths.bio;
    validBio = UserProfile.checkMaxLength({ bio }, bioMaxCharacterCount);
  }
  if (hasInfluencerFields && validInfluencerSettings) {
    const influencerWithSourceExists = (await userRepo.getUsersByUtmTagSource(source, KnownMediums.influencer))[0];
    if (influencerWithSourceExists && influencerWithSourceExists.uid !== user.uid) {
      throw new HandledError('Influencer with this source already exists');
    }
  }

  if (!validMobileNumber
    || !validEmail
    || !validSkillLevel
    || !validLongTermGoal
    || !validGender
    || !validDateOfBirth
    || !validAddressLine1
    || !validAddressLine2
    || !validPostCode
    || !validSuburb
    || !validUnitNum
    || !validState
    || !validWeight
    || !validHeight
    || !validNickname
    || !validFirstname
    || !validLastname
    || !validBio
  ) {
    return;
  }

  const userDetailsToUpdate = {
    ...(email && { email }),
    ...(mobileNumber && { mobileNumber }),
    ...(skillLevel && { skillLevel }),
    ...(longTermGoal && { longTermGoal }),
    ...(heartRateMonitorIdentifier && { heartRateMonitorIdentifier }),
    ...(gender && { gender }),
    ...(dateOfBirth && { dateOfBirth }),
    ...(addressLine1 && { addressLine1 }),
    ...(addressLine2 && { addressLine2 }),
    ...(postCode && { postCode }),
    ...(suburb && { suburb }),
    ...(unit && { unit }),
    ...(state && { state }),
    ...(weight && { weight }),
    ...(height && { height }),
    ...(nickname && { nickname }),
    ...(firstname && { firstname }),
    ...(lastname && { lastname }),
    ...(bio && { bio }),
    ...(isStreamingUser !== undefined && { isStreamingUser }),
  };

  const userUpdate = {
    ...user,
    ...userDetailsToUpdate,
  };

  if (!attributionSourceId) {
    delete userUpdate.signupSource?.attributionSourceId;
  } else {
    userUpdate.signupSource = { ...userUpdate.signupSource, attributionSourceId };
  }

  if (!signupSource) {
    delete userUpdate.signupSource?.source;
  } else {
    userUpdate.signupSource = { ...userUpdate.signupSource, source: signupSource };
  }

  if (hasInfluencerFields) {
    const influencerSettings = getInfluenerSettingsFromDiscountAndSource(discount, source, firstname, user.uid);
    userUpdate.influencerSettings = influencerSettings;
  } else {
    delete userUpdate.influencerSettings;
  }

  userUpdate.fullname = BaseUserProfile.buildFullname(userUpdate as UserProfile);
  return userUpdate;
}

const getInfluenerSettingsFromDiscountAndSource = (
  discount: number,
  source: string,
  firstname: string,
  uid: string,
): IInfluencerSettings => {
  const coupon = `${source}${discount}`;
  return {
    coupon,
    utmTags: {
      source,
      medium: 'influencer',
      campaign: coupon,
    },
    socialMetaTags: {
      title: `Join ${firstname} on Fitillion`,
      description: 'Tap Open to install the Fitillion app & start your free trial. '
        + 'Fitillion helps you meet your fitness goal with personalised workouts, meals & tracking',
      imageUrl: `https://res.cloudinary.com/fitbeat-operations/image/upload/influencer-images/${uid}/avatar`,
    },
  };
};

function formatMobileNumber(mobileNumber?: string): string {
  if (!mobileNumber) {
    return ' ';
  }

  if (mobileNumber === ' ' || mobileNumber.length < 8) {
    return mobileNumber;
  }

  const isAlreadyAnInternationalNumber = mobileNumber.startsWith('+') || mobileNumber.startsWith('00');
  if (isAlreadyAnInternationalNumber) {
    return mobileNumber;
  }

  if (mobileNumber.startsWith('0')) {
    return mobileNumber.replace(/0/, '+61');
  }
  return `+61${mobileNumber}`;
}
